import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, MessageCircle, Image as ImageIcon, Loader2, Share2 } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import { canShareToStory } from '@/utils/achievementCard';
import { shareRecognition, type RecognitionFacts, type ShareChannel } from '@/utils/recognitionCard';
import { getMealShareFacts } from '@/utils/mealMicros';

/**
 * «Поделиться приёмом» в Дневнике.
 *
 * ЗАЧЕМ (решение Славы 05.09.2026). На карточке из момента съёмки витаминов
 * нет физически: фото идёт через FAST-режим `analyze-meal`, который считает
 * шесть полей ради скорости, а микронутриенты досчитываются уже после записи.
 * Слава: «при сканировании фото в моменте он отправляет только КБЖУ, так
 * и оставим, но в дневник надо функцию добавить». В Дневнике приём давно
 * досчитан — там витамины есть всегда.
 *
 * ⚠️ ПОЧЕМУ НЕ ПЕРЕИСПОЛЬЗУЮ `ShareRecognition`. Тот компонент — ПРЕДЛОЖЕНИЕ
 * («Показать друзьям, как это считается?») с крестиком «больше не предлагать»
 * и отказом на 30 дней. Здесь другая природа: человек сам пришёл в Дневник
 * и сам нажал. Предлагать то, что уже попросили, — навязчивость наоборот,
 * а общий флаг отказа прятал бы и эту кнопку тоже.
 *
 * Свёрнут до одной строки: три канала разом утяжелили бы карточку приёма,
 * где и так «Отправить партнёру», «Узнать пользу» и «Удалить».
 */
/**
 * Призыв по типу приёма — «Поделиться обедом», а не безличное «Поделиться».
 * Предложение Славы 05.09.2026: конкретика цепляет сильнее общего слова,
 * человек сразу понимает, ЧЕМ делится, и это не требует от него догадки.
 */
const SHARE_LABEL: Record<string, string> = {
  breakfast: 'Поделиться завтраком',
  lunch: 'Поделиться обедом',
  dinner: 'Поделиться ужином',
  snack: 'Поделиться перекусом',
};

export function ShareMealButton({ mealId, mealType }: { mealId: string; mealType?: string | null }) {
  const { profile } = useUserStore();
  const { haptic } = useTelegram();
  const [facts, setFacts] = useState<RecognitionFacts | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<ShareChannel | null>(null);

  if (!profile?.id) return null;

  async function open() {
    haptic('light');
    if (facts) { setFacts(null); return; }
    setLoading(true);
    try {
      const f = await getMealShareFacts(mealId);
      if (f && f.calories > 0) setFacts(f);
      else haptic('error');
    } finally {
      setLoading(false);
    }
  }

  async function share(channel: ShareChannel) {
    if (!facts || !profile?.id) return;
    haptic('light');
    setBusy(channel);
    try {
      await shareRecognition(channel, facts, {
        userId: profile.id,
        name: profile.first_name,
        refCode: profile.ref_code,
      });
    } catch (e) {
      console.error('[ShareMealButton]', e);
      haptic('error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mt-2">
      <button
        onClick={open}
        disabled={loading}
        className="w-full py-2 rounded-lg text-xs text-sky-glow bg-sky-glow/5 border border-sky-glow/20 hover:bg-sky-glow/10 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 size={12} className="animate-spin" />
            Собираю карточку…
          </>
        ) : (
          <>
            <Share2 size={12} />
            {SHARE_LABEL[String(mealType)] ?? 'Поделиться приёмом'}
          </>
        )}
      </button>

      <AnimatePresence>
        {facts && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-2">
              {/* Говорим, что именно уедет на картинку: человек делится
                  публично и вправе знать это ДО нажатия, а не после. */}
              <div className="text-[10px] text-ink-dim text-center mb-1.5">
                {facts.micros?.length
                  ? `КБЖУ, состав и ${facts.micros.length} ${facts.micros.length === 1 ? 'нутриент' : 'нутриента'} от дневной нормы`
                  : 'КБЖУ и состав приёма'}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {canShareToStory() && (
                  <Btn Icon={Send} label="В историю" busy={busy === 'story'} disabled={!!busy} onClick={() => share('story')} accent />
                )}
                <Btn Icon={MessageCircle} label="Другу" busy={busy === 'chat'} disabled={!!busy} onClick={() => share('chat')} />
                <Btn Icon={ImageIcon} label="Картинкой" busy={busy === 'image'} disabled={!!busy} onClick={() => share('image')} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Btn({ Icon, label, onClick, busy, disabled, accent }: {
  Icon: any; label: string; onClick: () => void; busy: boolean; disabled: boolean; accent?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`py-2.5 rounded-xl flex flex-col items-center gap-1 text-[11px] font-medium transition-all active:scale-[0.97] disabled:opacity-50 ${
        accent ? 'bg-sky-glow/15 border border-sky-glow/35 text-sky-glow' : 'bg-bg-elevated border border-line text-ink-muted'
      }`}
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />}
      {label}
    </button>
  );
}
